import { ORDER_STATUSES } from "@/constants/orders";
import { workspaceSectionPath } from "@/constants/routes";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { InvoiceStatusBadge, OrderStatusBadge, SettlementBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SettlePaymentDialog } from "@/features/orders/settle-payment-dialog";
import {
  ReturnEquipmentDialog,
  orderOutstandingUnits,
} from "@/features/orders/return-equipment-dialog";
import { InvoiceActions } from "@/features/invoices/invoice-actions";
import { InvoiceDocument } from "@/features/invoices/invoice-document";
import { useCancelOrder, useOrder } from "@/features/orders/api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { useWorkspace } from "@/providers/workspace-provider";
import { PAYMENT_METHOD_LABELS } from "@/constants/orders";
import { ArrowLeft, RotateCcw, Wallet } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { INVOICE_KIND_LABELS, type InvoicePageSizeId } from "@bizy/core";

/** `/w/:slug/orders/:orderId` — order detail, settlement and invoice output. */
export const OrderDetailPage = () => {
  const { workspace, settings } = useWorkspace();
  const { orderId } = useParams<{ orderId: string }>();
  const orderQuery = useOrder(workspace.id, orderId);
  const cancelOrder = useCancelOrder(workspace.id, orderId ?? "");
  const [settleOpen, setSettleOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [pageSize, setPageSize] = useState<InvoicePageSizeId | null>(null);
  const invoiceRef = useRef<HTMLDivElement | null>(null);

  if (orderQuery.isLoading) return <LoadingState label="Loading order…" />;
  if (orderQuery.isError || !orderQuery.data) {
    return (
      <ErrorState
        description={orderQuery.error instanceof Error ? orderQuery.error.message : "Order unavailable."}
        onRetry={() => void orderQuery.refetch()}
      />
    );
  }

  const order = orderQuery.data;
  const latestInvoice = order.invoices.at(-1) ?? null;
  const activePageSize = pageSize ?? latestInvoice?.page_size ?? "a4";
  const workspaceSettings = settings;
  const outstandingUnits = orderOutstandingUnits(order);

  return (
    <>
      <PageHeader
        title={order.code}
        description={`${order.customer_name}${order.customer_phone ? ` · ${order.customer_phone}` : ""}`}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to={workspaceSectionPath(workspace.slug, "orders")}>
                <ArrowLeft className="size-4" aria-hidden />
                Back
              </Link>
            </Button>
            {outstandingUnits > 0 && order.status !== ORDER_STATUSES.cancelled ? (
              <Button size="sm" variant="outline" onClick={() => setReturnOpen(true)}>
                <RotateCcw className="size-4" aria-hidden />
                Return equipment
              </Button>
            ) : null}
            {order.balance_amount > 0 && order.status !== ORDER_STATUSES.cancelled ? (
              <Button size="sm" onClick={() => setSettleOpen(true)}>
                <Wallet className="size-4" aria-hidden />
                Settle payment
              </Button>
            ) : null}
            {order.status !== ORDER_STATUSES.cancelled ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-danger"
                loading={cancelOrder.isPending}
                onClick={async () => {
                  try {
                    await cancelOrder.mutateAsync(undefined);
                    toast.success("Order cancelled.");
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : "Could not cancel.");
                  }
                }}
              >
                Cancel order
              </Button>
            ) : null}
          </>
        }
      />

      {order.returned_at ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
            <span>
              Returned on {formatDateTime(order.returned_at)}
              {order.return_note ? ` · ${order.return_note}` : ""}
            </span>
            <span className="text-muted-foreground">
              {outstandingUnits > 0 ? `${outstandingUnits} units still out` : "All units back in stock"}
            </span>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Total", value: formatCurrency(order.total_amount, order.currency) },
          { label: "Paid", value: formatCurrency(order.paid_amount, order.currency) },
          { label: "Balance", value: formatCurrency(order.balance_amount, order.currency) },
          { label: "Status", value: <OrderStatusBadge status={order.status} /> },
        ].map((item) => (
          <Card key={item.label}>
            <CardContent className="space-y-1 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{item.label}</p>
              <p className="text-lg font-semibold tabular-nums">{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.name_snapshot}
                      {item.variant_snapshot ? (
                        <span className="text-muted-foreground"> · {item.variant_snapshot}</span>
                      ) : null}
                      {item.bundle_components?.length ? (
                        <p className="text-xs text-muted-foreground">
                          {item.bundle_components
                            .map((component) => `${component.name} × ${component.quantity}`)
                            .join(" · ")}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(item.unit_price, order.currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(item.line_total, order.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {order.payments.length === 0 ? (
              <EmptyState title="No payment recorded" description="This order is fully outstanding." />
            ) : (
              order.payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between gap-3 text-sm">
                  <div>
                    <p className="font-medium tabular-nums">
                      {formatCurrency(payment.amount, order.currency)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {PAYMENT_METHOD_LABELS[payment.method]}
                      {payment.reference ? ` · ${payment.reference}` : ""}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(payment.settled_at)}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Invoices</CardTitle>
          <SettlementBadge type={order.settlement_type} />
        </CardHeader>
        <CardContent className="space-y-4">
          {order.invoices.length === 0 ? (
            <EmptyState title="No invoice yet" description="Invoices are issued as payments settle." />
          ) : (
            order.invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="space-y-1">
                  <p className="font-medium">{invoice.number}</p>
                  <p className="text-xs text-muted-foreground">
                    {INVOICE_KIND_LABELS[invoice.kind]} ·{" "}
                    {formatCurrency(invoice.total_amount, invoice.currency)} ·{" "}
                    {formatDateTime(invoice.issued_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <InvoiceStatusBadge status={invoice.status} />
                  <Link
                    to={`/w/${workspace.slug}/invoices/${invoice.id}/print`}
                    className="text-sm text-primary"
                  >
                    Open
                  </Link>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {latestInvoice ? (
        <Card>
          <CardHeader>
            <CardTitle>Invoice output</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InvoiceActions
              workspaceId={workspace.id}
              workspaceSlug={workspace.slug}
              invoice={latestInvoice}
              documentRef={invoiceRef}
              onPageSizeChange={(value) => setPageSize(value)}
            />
            <div className="overflow-hidden rounded-lg border border-border bg-white p-4">
              <InvoiceDocument
                ref={invoiceRef}
                invoice={{ ...latestInvoice, page_size: activePageSize }}
                order={order}
                items={order.items}
                workspace={workspace}
                settings={workspaceSettings}
                locale="id"
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <SettlePaymentDialog
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        workspaceId={workspace.id}
        order={order}
      />

      <ReturnEquipmentDialog
        open={returnOpen}
        onClose={() => setReturnOpen(false)}
        workspaceId={workspace.id}
        order={order}
      />
    </>
  );
};
